import { useState, useEffect, useCallback } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../utils/firebase';
import { Study } from '../types/study.types';
import { retryFirebaseCall } from '../utils/retry';

interface APIResponse<T> {
  success: boolean;
  data?: T;
  message?: string;
  error?: string;
}

interface UseStudyReturn {
  study: Study | null;
  loading: boolean;
  error: string | null;
  updateStudy: (updates: Partial<Study>) => Promise<void>;
  toggleEnrollment: () => Promise<void>;
  activateStudy: () => Promise<void>;
  pauseStudy: () => Promise<void>;
  completeStudy: () => Promise<void>;
}

export const useStudy = (studyId: string): UseStudyReturn => {
  const [study, setStudy] = useState<Study | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Real-time listener for single study
  useEffect(() => {
    if (!studyId) {
      setLoading(false);
      setError('Study ID is required');
      return;
    }

    const unsubscribe = onSnapshot(
      doc(db, 'studies', studyId),
      (snapshot) => {
        if (snapshot.exists()) {
          setStudy({ ...snapshot.data(), id: snapshot.id } as Study);
          setError(null);
        } else {
          setError('Study not found');
          setStudy(null);
        }
        setLoading(false);
      },
      (err) => {
        console.error('Error fetching study:', err);
        setError(err.message || 'Failed to fetch study');
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [studyId]);

  /**
   * Update study properties
   */
  const updateStudy = useCallback(async (updates: Partial<Study>): Promise<void> => {
    if (!studyId) {
      throw new Error('Study ID is required');
    }

    setError(null);

    try {
      const updateStudyFn = httpsCallable<
        { studyId: string; updates: Partial<Study> },
        APIResponse<void>
      >(functions, 'updateStudy');

      const result = await retryFirebaseCall(
        () => updateStudyFn({ studyId, updates }),
        'updateStudy'
      );

      if (!result.data.success) {
        throw new Error(result.data.error || 'Failed to update study');
      }
    } catch (err: any) {
      const errorMessage = err.message || 'Failed to update study';
      setError(errorMessage);
      throw new Error(errorMessage);
    }
  }, [studyId]);

  /**
   * Toggle enrollment open/closed
   */
  const toggleEnrollment = useCallback(async (): Promise<void> => {
    if (!study) {
      throw new Error('Study not loaded');
    }

    await updateStudy({
      enrollmentSettings: {
        ...study.enrollmentSettings,
        isOpen: !study.enrollmentSettings.isOpen,
      },
    } as Partial<Study>);
  }, [study, updateStudy]);

  /**
   * Activate a draft study
   */
  const activateStudy = useCallback(async (): Promise<void> => {
    if (!studyId) {
      throw new Error('Study ID is required');
    }

    setError(null);

    try {
      const activateStudyFn = httpsCallable<
        { studyId: string },
        APIResponse<void>
      >(functions, 'activateStudy');

      const result = await retryFirebaseCall(
        () => activateStudyFn({ studyId }),
        'activateStudy'
      );

      if (!result.data.success) {
        throw new Error(result.data.error || 'Failed to activate study');
      }
    } catch (err: any) {
      const errorMessage = err.message || 'Failed to activate study';
      setError(errorMessage);
      throw new Error(errorMessage);
    }
  }, [studyId]);

  /**
   * Pause an active study
   */
  const pauseStudy = useCallback(async (): Promise<void> => {
    await updateStudy({ status: 'paused' } as Partial<Study>);
  }, [updateStudy]);

  /**
   * Mark study as completed
   */
  const completeStudy = useCallback(async (): Promise<void> => {
    await updateStudy({
      status: 'completed',
      enrollmentSettings: {
        ...study?.enrollmentSettings,
        isOpen: false,
      },
    } as Partial<Study>);
  }, [study, updateStudy]);

  return {
    study,
    loading,
    error,
    updateStudy,
    toggleEnrollment,
    activateStudy,
    pauseStudy,
    completeStudy,
  };
};
